/**
 * 前端权限工具 - 全面细粒度控制
 * 所有页面/按钮/路由都走 hasPermission 检查，不依赖 userLevel 硬编码
 */

// ===== 权限常量 =====
export const PERMS = {
  VEHICLE_VIEW: 'vehicle:view',
  VEHICLE_CREATE: 'vehicle:create',
  VEHICLE_EDIT: 'vehicle:edit',
  VEHICLE_DELETE: 'vehicle:delete',
  VEHICLE_ARCHIVE: 'vehicle:archive',
  VEHICLE_EXPORT: 'vehicle:export',

  DRIVER_VIEW: 'driver:view',
  DRIVER_CREATE: 'driver:create',
  DRIVER_EDIT: 'driver:edit',
  DRIVER_DELETE: 'driver:delete',
  DRIVER_IMPORT: 'driver:import',

  INSURANCE_VIEW: 'insurance:view',
  INSURANCE_CREATE: 'insurance:create',
  INSURANCE_EDIT: 'insurance:edit',
  INSURANCE_DELETE: 'insurance:delete',
  INSURANCE_COMPANY_VIEW: 'insurance:company:view',
  INSURANCE_COMPANY_EDIT: 'insurance:company:edit',

  REBATE_VIEW: 'rebate:view',
  REBATE_RULE_VIEW: 'rebate:rule:view',
  REBATE_RULE_EDIT: 'rebate:rule:edit',

  SETTLEMENT_VIEW: 'settlement:view',
  SETTLEMENT_CONFIRM: 'settlement:confirm',
  SETTLEMENT_UNDO: 'settlement:undo',

  INSPECTION_VIEW: 'inspection:view',
  INSPECTION_EDIT: 'inspection:edit',
  INSPECTION_DELETE: 'inspection:delete',

  COMPANY_VIEW: 'company:view',
  COMPANY_EDIT: 'company:edit',
  USER_MANAGE: 'user:manage',

  SUBMISSION_VIEW: 'submission:view',
  SUBMISSION_REVIEW: 'submission:review',
  SUBMISSION_DELETE: 'submission:delete',

  TRAINING_VIEW: 'training:view',
  TRAINING_MANAGE: 'training:edit',
  TRAINING_REFUND: 'training:refund',
};

// ===== 角色默认权限模板 =====
const ROLE_DEFAULT_PERMS = {
  super_admin: [...Object.values(PERMS)],
  admin: [...Object.values(PERMS)],
  管理: Object.values(PERMS).filter(p => p !== PERMS.USER_MANAGE && p !== PERMS.COMPANY_VIEW && p !== PERMS.COMPANY_EDIT),
  company_admin: [
    PERMS.VEHICLE_VIEW, PERMS.VEHICLE_CREATE, PERMS.VEHICLE_EDIT, PERMS.VEHICLE_ARCHIVE, PERMS.VEHICLE_EXPORT,
    PERMS.DRIVER_VIEW, PERMS.DRIVER_CREATE, PERMS.DRIVER_EDIT, PERMS.DRIVER_IMPORT,
    PERMS.INSURANCE_VIEW, PERMS.INSURANCE_CREATE, PERMS.INSURANCE_EDIT, PERMS.INSURANCE_DELETE,
    PERMS.INSURANCE_COMPANY_VIEW, PERMS.INSURANCE_COMPANY_EDIT,
    PERMS.REBATE_VIEW, PERMS.REBATE_RULE_VIEW, PERMS.REBATE_RULE_EDIT,
    PERMS.SETTLEMENT_VIEW, PERMS.SETTLEMENT_CONFIRM, PERMS.SETTLEMENT_UNDO,
    PERMS.INSPECTION_VIEW, PERMS.INSPECTION_EDIT, PERMS.INSPECTION_DELETE,
    PERMS.SUBMISSION_VIEW, PERMS.SUBMISSION_REVIEW, PERMS.SUBMISSION_DELETE,
    PERMS.TRAINING_VIEW, PERMS.TRAINING_MANAGE, PERMS.TRAINING_REFUND,
  ],
  city_admin: [
    PERMS.VEHICLE_VIEW, PERMS.VEHICLE_CREATE, PERMS.VEHICLE_EDIT, PERMS.VEHICLE_ARCHIVE, PERMS.VEHICLE_EXPORT,
    PERMS.DRIVER_VIEW, PERMS.DRIVER_CREATE, PERMS.DRIVER_EDIT, PERMS.DRIVER_IMPORT,
    PERMS.INSURANCE_VIEW, PERMS.INSURANCE_CREATE, PERMS.INSURANCE_EDIT,
    PERMS.INSPECTION_VIEW, PERMS.INSPECTION_EDIT,
    PERMS.SUBMISSION_VIEW, PERMS.SUBMISSION_REVIEW,
    PERMS.TRAINING_VIEW,
  ],
  user: [PERMS.VEHICLE_VIEW, PERMS.DRIVER_VIEW, PERMS.INSURANCE_VIEW, PERMS.INSPECTION_VIEW],
};

// ===== 角色信息 =====
export const ROLE_OPTIONS = [
  { value: 'super_admin', label: '超级管理员', color: 'red' },
  { value: '管理', label: '管理员', color: 'purple' },
  { value: 'city_admin', label: '城市管理员', color: 'orange' },
  { value: 'company_admin', label: '公司管理员', color: 'blue' },
  { value: 'user', label: '普通用户', color: 'default' },
];

export function getRoleLabel(role) {
  const r = ROLE_OPTIONS.find(r => r.value === role);
  return r ? r.label : role;
}

export function getRoleColor(role) {
  const r = ROLE_OPTIONS.find(r => r.value === role);
  return r ? r.color : 'default';
}

export function getDefaultPerms(role) {
  return ROLE_DEFAULT_PERMS[role] || ROLE_DEFAULT_PERMS.user;
}

// ===== 核心权限检查 =====
/**
 * 检查当前用户是否有指定权限
 * @param {string} perm - 权限key，如 'vehicle:edit'
 * @returns {boolean}
 */
export function hasPermission(perm) {
  if (!perm) return true; // 不传权限 = 放行

  // 角色兜底：admin/super_admin 拥有全部权限
  const role = localStorage.getItem('role');
  if (role === 'admin' || role === 'super_admin') return true;
  // 管理 角色：除 user:manage / company:* 外全部放行
  if (role === '管理') {
    if (!['user:manage', 'company:view', 'company:edit'].includes(perm)) return true;
    return false;
  }

  // 从 localStorage 读取当前用户的 permissions 列表
  try {
    const permsStr = localStorage.getItem('permissions');
    if (!permsStr) {
      // 降级到 role 判断
      const defaults = getDefaultPerms(role);
      return defaults.includes(perm);
    }
    const perms = JSON.parse(permsStr);
    return perms.includes(perm);
  } catch {
    return false;
  }
}

// ===== 便捷函数 =====
export function canViewVehicles() { return hasPermission(PERMS.VEHICLE_VIEW); }
export function canCreateVehicle() { return hasPermission(PERMS.VEHICLE_CREATE); }
export function canEditVehicle() { return hasPermission(PERMS.VEHICLE_EDIT); }
export function canDeleteVehicle() { return hasPermission(PERMS.VEHICLE_DELETE); }
export function canArchiveVehicle() { return hasPermission(PERMS.VEHICLE_ARCHIVE); }
export function canExportVehicle() { return hasPermission(PERMS.VEHICLE_EXPORT); }

export function canViewDrivers() { return hasPermission(PERMS.DRIVER_VIEW); }
export function canCreateDriver() { return hasPermission(PERMS.DRIVER_CREATE); }
export function canEditDriver() { return hasPermission(PERMS.DRIVER_EDIT); }
export function canDeleteDriver() { return hasPermission(PERMS.DRIVER_DELETE); }
export function canImportDriver() { return hasPermission(PERMS.DRIVER_IMPORT); }

export function canViewInsurance() { return hasPermission(PERMS.INSURANCE_VIEW); }
export function canCreateInsurance() { return hasPermission(PERMS.INSURANCE_CREATE); }
export function canEditInsurance() { return hasPermission(PERMS.INSURANCE_EDIT); }
export function canDeleteInsurance() { return hasPermission(PERMS.INSURANCE_DELETE); }
export function canViewInsuranceCompany() { return hasPermission(PERMS.INSURANCE_COMPANY_VIEW); }
export function canEditInsuranceCompany() { return hasPermission(PERMS.INSURANCE_COMPANY_EDIT); }

export function canViewRebate() { return hasPermission(PERMS.REBATE_VIEW); }
export function canViewRebateRule() { return hasPermission(PERMS.REBATE_RULE_VIEW); }
export function canEditRebateRule() { return hasPermission(PERMS.REBATE_RULE_EDIT); }

export function canViewSettlement() { return hasPermission(PERMS.SETTLEMENT_VIEW); }
export function canConfirmSettlement() { return hasPermission(PERMS.SETTLEMENT_CONFIRM); }
export function canUndoSettlement() { return hasPermission(PERMS.SETTLEMENT_UNDO); }

export function canViewInspection() { return hasPermission(PERMS.INSPECTION_VIEW); }
export function canEditInspection() { return hasPermission(PERMS.INSPECTION_EDIT); }
export function canDeleteInspection() { return hasPermission(PERMS.INSPECTION_DELETE); }

export function canViewCompany() { return hasPermission(PERMS.COMPANY_VIEW); }
export function canEditCompany() { return hasPermission(PERMS.COMPANY_EDIT); }
export function canManageUser() { return hasPermission(PERMS.USER_MANAGE); }

export function canViewSubmission() { return hasPermission(PERMS.SUBMISSION_VIEW); }
export function canReviewSubmission() { return hasPermission(PERMS.SUBMISSION_REVIEW); }
export function canDeleteSubmission() { return hasPermission(PERMS.SUBMISSION_DELETE); }

export function canViewTraining() { return hasPermission(PERMS.TRAINING_VIEW); }
export function canManageTraining() { return hasPermission(PERMS.TRAINING_MANAGE); }
export function canRefundTraining() { return hasPermission(PERMS.TRAINING_REFUND); }

// ===== 兼容旧函数 =====
export function getUserLevel() {
  try {
    const level = localStorage.getItem('user_level');
    if (level) return parseInt(level);
    const role = localStorage.getItem('role');
    if (role === 'super_admin' || role === 'admin') return 1;
    if (role === '管理') return 2;
    if (role === 'company_admin') return 3;
    if (role === 'city_admin') return 4;
    return 5;
  } catch { return 5; }
}

export function isSuperAdmin() {
  return getUserLevel() === 1;
}

export function isManager() {
  return getUserLevel() === 2;
}

export function isCompanyAdmin() {
  return getUserLevel() === 3;
}

export function isCityAdmin() {
  return getUserLevel() === 4;
}

export function canAdmin() {
  return hasPermission(PERMS.USER_MANAGE);
}

export function canEdit() {
  return hasPermission(PERMS.VEHICLE_EDIT);
}
